import { FeatureFlagKey } from "@redotech/feature-flags-model";
import { useRequiredContext } from "@redotech/react-util/context";
import { useLoad } from "@redotech/react-util/load";
import { AuthContext } from "@redotech/redo-merchant-app-common/auth";
import { RedoMerchantClientContext } from "@redotech/redo-merchant-app-common/client/context";
import { REDO_EFFECT_API_URL } from "@redotech/redo-merchant-app-common/config";
import {
  EffectApiClientContext,
  EffectApiClientProvider,
  type EffectApiContext,
} from "@redotech/redo-merchant-app-common/effect-api-client-provider";
import { RedoPlatformAuthRpcClientContext } from "@redotech/redo-merchant-app-common/platform/redo-platform-rpc-client-provider";
import { RedoMerchantRpcClientContext } from "@redotech/redo-merchant-app-common/rpc-client";
import { reactQueryClient } from "@redotech/redo-web/rpc-query/react-query-config";
import {
  createContext,
  memo,
  type ReactNode,
  useContext,
  useMemo,
  useRef,
} from "react";
import { createGlobalConversationCollections } from "../db/collections";
import { NotificationSyncHandlers } from "../stream/notification-sync-handlers";
import { TeamNotificationStreamProvider } from "../stream/team-notification-stream-provider";
import { GlobalConversationCollectionsContext } from "./global-conversation-collections-context";

const GlobalConversationStoreProvider = memo(
  function GlobalConversationStoreProvider({
    children,
  }: {
    children: ReactNode;
  }) {
    const effectApiClient = useRequiredContext(EffectApiClientContext);
    const rpcClient = useRequiredContext(RedoMerchantRpcClientContext);
    const merchantClient = useRequiredContext(RedoMerchantClientContext);

    // Keep a ref to the latest effectApi so the stable proxy always calls
    // through to the current (non-disposed) runtime.
    const effectApiRef = useRef(effectApiClient);
    effectApiRef.current = effectApiClient;

    const stableEffectApi = useMemo<EffectApiContext>(
      () => ({
        runPromise: (...args) => effectApiRef.current.runPromise(...args),
        getClient: () => effectApiRef.current.getClient(),
      }),
      // Stable proxy — intentionally created once, reads from ref at call time.

      [],
    );

    const collections = useMemo(() => {
      return createGlobalConversationCollections({
        queryClient: reactQueryClient,
        effectApi: stableEffectApi,
        rpcClient,
        merchantClient,
      });
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [stableEffectApi]);

    return (
      <GlobalConversationCollectionsContext.Provider value={collections}>
        <NotificationSyncHandlers />
        {children}
      </GlobalConversationCollectionsContext.Provider>
    );
  },
);

interface SupportProvidersProps {
  children: ReactNode;
}

let cachedFeatureFlagEnabled: boolean | null = null;
let featureFlagPromise: Promise<boolean> | null = null;

const FeatureFlagEnabledContext = createContext<boolean | undefined>(undefined);
FeatureFlagEnabledContext.displayName = "FeatureFlagEnabledContext";

export function useFeatureFlagEnabled(): boolean {
  return useRequiredContext(FeatureFlagEnabledContext);
}

const FeatureFlagEnabledProvider = memo(function FeatureFlagEnabledProvider({
  children,
}: {
  children: ReactNode;
}) {
  const platformRpcClient = useRequiredContext(
    RedoPlatformAuthRpcClientContext,
  );

  const featureFlagLoad = useLoad(async () => {
    if (cachedFeatureFlagEnabled !== null) {
      return cachedFeatureFlagEnabled;
    }

    if (featureFlagPromise === null) {
      featureFlagPromise = platformRpcClient
        .getFeatureFlag({ flagKey: FeatureFlagKey.SUPPORT_HELPDESK_V2 })
        .then((response) => {
          cachedFeatureFlagEnabled = response.enabled;
          return response.enabled;
        });
    }

    return featureFlagPromise;
  }, []);

  if (featureFlagLoad.pending) {
    return null;
  }

  return (
    <FeatureFlagEnabledContext.Provider value={featureFlagLoad.value ?? false}>
      {children}
    </FeatureFlagEnabledContext.Provider>
  );
});

export const SupportProviders = memo(function SupportProviders({
  children,
}: SupportProvidersProps) {
  const auth = useContext(AuthContext);

  return (
    <EffectApiClientProvider
      authToken={auth?.token}
      baseUrl={REDO_EFFECT_API_URL}
    >
      <TeamNotificationStreamProvider
        topics={[
          "notifications",
          "presence",
          "ai-activity",
          "user-notifications",
        ]}
      >
        <GlobalConversationStoreProvider>
          <FeatureFlagEnabledProvider>{children}</FeatureFlagEnabledProvider>
        </GlobalConversationStoreProvider>
      </TeamNotificationStreamProvider>
    </EffectApiClientProvider>
  );
});
